/**
 * @author NikolaV
 */

import * as React from 'react';
import { makeObservable } from 'mobx';
import { observer } from 'mobx-react';
import { LogpointsService } from '@shared/logpoints/LogpointsService';
import { Dropdown } from '@shared/ui-react/Dropdown';
import { Button } from '@shared/ui-react/Button';
import { MapWrapper } from '@openlayers/MapWrapper';
import { Tooltip } from 'primereact/tooltip';
import { LogpointFeatureFactory } from '@openlayers/logpoints/LogpointFeatureFactory';


const __ = kuura.getMessage;


@observer
export class LogpointsToolbar extends React.Component<{
    mapWrapper: MapWrapper
    onBulkEdit?: () => void,
    onDrawLinecode?: () => void,
    onDownload?: () => void,
}> {
    dropdownEl: Dropdown = null;

    constructor(props: any) {
        super(props);
        makeObservable(this);
    }

    readonly labelPropOptions = {
        dz: 'dZ',
        x: 'X',
        y: 'Y',
        z: 'Z',
        xyz: 'XYZ',
        ...LogpointsService.propertyTranslations
    };

    render() {
        const { mapWrapper } = this.props;
        const colorDesc = LogpointsService.colorAndDescription;
        const hasAnyAction = !!this.props.onBulkEdit || !!this.props.onDrawLinecode || !!this.props.onDownload;

        return (
            <>
                <Button secondary subtle icon='iki-fit-to-screen' onClick={mapWrapper.fitLogpointsLayer} title="Fit loaded logpoints into view"/>

                <Dropdown ref={el => this.dropdownEl = el} closeOnBodyClick={!hasAnyAction} buttonStyle={{ paddingLeft: 10, paddingRight: 10 }} className='ik-button-secondary--subtle'>
                    <i className="iki iki-more"/>
                    <div className="no-select">
                        { hasAnyAction && kuura.hasPrivilege('Edit_LogPoint') && <>
                            <div className="dropdown-list-title">{__('general.actions')}:</div>
                            <ul className="dropdown-list clickable" onClick={() => this.dropdownEl.close(true)}>
                                {!!this.props.onBulkEdit && <li onClick={this.props.onBulkEdit}><i className="iki iki-area"></i><span>{__('js_map.bulk_edit')}</span></li>}
                                {!!this.props.onDrawLinecode && <li onClick={this.props.onDrawLinecode}><i className='iki iki-edit'/> {__('js_map.linecode.draw_line')}</li> }
                                {!!this.props.onDownload && kuura.hasPrivilege('Download_LogPoint') && <li onClick={this.props.onDownload}><i className='iki iki-download'/> {__('general.download')}</li> }
                            </ul>
                        </>}

                        <div className="dropdown-list-title">{__('general.settings')}:</div>
                        <ul className="dropdown-list clickable">
                            <li onClick={this.layer.toggleShowLines}><input type="checkbox" checked={this.showLines} readOnly/>{__('js_map.logpoints.show_lines')}</li>
                        </ul>
                        <div className="dropdown-list-title">{__('js_map.logpoints.color_mode')}:</div>
                        <ul className="dropdown-list clickable">
                            <ColorTooltip target=".logpoints_heatmap_help" colors={[colorDesc.newer, colorDesc.older]}/>
                            <li onClick={() => this.setColorFunction('timestamp_color_ramp')}>
                                <input type="radio" checked={this.getColorFunction == 'timestamp_color_ramp'} readOnly/>
                                <span>{__('js_map.logpoints.color.timestamp_color_ramp')} <i style={{ fontSize: 14, position: 'relative', top: 2 }} className="iki iki-help logpoints_heatmap_help"/></span>
                            </li>

                            <ColorTooltip target=".logpoints_tolerance_help" colors={[colorDesc.in_tolerance, colorDesc.under_tolerance, colorDesc.not_in_tolerance]}/>
                            <li onClick={() => this.setColorFunction('tolerance')}>
                                <input type="radio" checked={this.getColorFunction == 'tolerance'} readOnly/>
                                <span>{__('js_map.logpoints.color.tolerance')} <i style={{ fontSize: 14, position: 'relative', top: 2 }} className="iki iki-help logpoints_tolerance_help"/></span>
                            </li>

                            <ColorTooltip target=".logpoints_tolerance_and_approval_help" colors={[colorDesc.in_tolerance, colorDesc.under_tolerance, colorDesc.not_in_tolerance, colorDesc.approved, colorDesc.checked]}/>
                            <li onClick={() => this.setColorFunction('tolerance_approval')}>
                                <input type="radio" checked={this.getColorFunction == 'tolerance_approval'} readOnly/>
                                <span>{__('js_map.logpoints.color.tolerance_and_approval')} <i style={{ fontSize: 14, position: 'relative', top: 2 }} className="iki iki-help logpoints_tolerance_and_approval_help"/></span>
                            </li>
                        </ul>
                        <div className="dropdown-list-title">{__('js_map.logpoints.logpoint_size')}:</div>
                        <ul className="dropdown-list clickable">
                            <li onClick={() => this.setIconScale(8)}><input type="radio" checked={this.iconScale == 8} readOnly/>{__('general.size.normal')}</li>
                            <li onClick={() => this.setIconScale(13)}><input type="radio" checked={this.iconScale == 13} readOnly/>{__('general.size.large')}</li>
                        </ul>
                        <Tooltip target=".logpoints_point_label_icon" baseZIndex={99999}>
                            <div>{__('js_map.labels_are_shows_when_zoomed_in')}</div>
                            <div style={{ marginTop: 15 }}>{__('js_map.labels.shift_click_to_select_multiple')}</div>
                        </Tooltip>
                        <div className="dropdown-list-title flex-row ai-center">{__('js_map.logpoints.logpoint_label')}&nbsp;<i className="iki iki-help logpoints_point_label_icon"/>:</div>
                        <ul className="dropdown-list clickable">
                            {Object.entries(this.labelPropOptions).map(([prop, label]) => {
                                return <li key={prop} onClick={e => this.setLabelProp(e, prop)} title={__('js_map.labels.shift_click_to_select_multiple')}>
                                    <input type="radio" checked={this.labelProps.includes(prop)} readOnly/>{label}
                                </li>;
                            })}
                            <li onClick={() => this.layer.setNoNamesInLabels(!this.layer.noNamesInLabel)}><input type="checkbox" checked={this.layer.noNamesInLabel} readOnly/>{__('js_map.logpoints.dont_show_property_names')}</li>
                        </ul>
                    </div>
                </Dropdown>
            </>
        );
    }

    private setLabelProp = (event, prop: string) => {
        LogpointFeatureFactory.clearCache();
        if (event.altKey || event.shiftKey || event.ctrlKey) {
            this.layer.toggleLabelProp(prop);
        } else {
            this.layer.setLabelProps(prop);
        }
    }

    private setColorFunction = (fn: LogpointColorFunction) => this.layer.setColorFunction(fn);

    get showLines() {
        return this.layer.showLines;
    }

    get getColorFunction(): LogpointColorFunction {
        return this.layer.colorFunctionName;
    }

    get labelProps() {
        return this.layer.labelProps;
    }

    private setIconScale = (scale: number) => this.layer.setIconScale(scale);

    get iconScale() {
        return this.layer.iconScale;
    }

    get layer() {
        return this.props.mapWrapper.featureLayers.logpoints;
    }
}


const roverCanvas = LogpointFeatureFactory.renderIconCanvas({ type: 'rover' } as any, 15, 'white', false).toDataURL();
const vehicleCanvas = LogpointFeatureFactory.renderIconCanvas({ type: 'vehicle' } as any, 15, 'white', false).toDataURL();
const tsCanvas = LogpointFeatureFactory.renderIconCanvas({ type: 'total_station' } as any, 15, 'white', false).toDataURL();

function ColorTooltip({ target, colors }) {
    return <Tooltip target={target} baseZIndex={99999}>
        {colors.map(c =>
            <div key={c[1]} className="flex-row ai-center" style={{ fontSize: 13 }}>
                <div style={{ width: 15, height: 15, background: c[0] }}></div>
                &nbsp;&nbsp;{c[1]}</div>
        )}
        <div className="flex-row ai-center" style={{ fontSize: 13, marginTop: 15 }}>
            <div style={{ width: 15, height: 15, background: `url(${roverCanvas}) no-repeat`, backgroundSize: '15px' }}></div>
            &nbsp;&nbsp;{__('logpoints.type.ROVER_GPS_js')} / {__('logpoints.type.SIMULATOR_js')}
        </div>
        <div className="flex-row ai-center" style={{ fontSize: 13 }}>
            <div style={{ width: 15, height: 15, background: `url(${vehicleCanvas}) no-repeat`, backgroundSize: '15px' }}></div>
            &nbsp;&nbsp;{__('logpoints.type.VEHICLE_js')}
        </div>
        <div className="flex-row ai-center" style={{ fontSize: 13 }}>
            <div style={{ width: 15, height: 15, background: `url(${tsCanvas}) no-repeat`, backgroundSize: '15px' }}></div>
            &nbsp;&nbsp;{__('logpoints.type.TOTAL_STATION_js')}
        </div>
    </Tooltip>
}
